import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
    X,
    Wrench,
    Activity,
    FileText,
    BarChart2,
    Play,
    Square,
    RotateCcw,
    Trash2,
    RefreshCw,
    Loader2,
    Cpu,
    Server,
    HardDrive
} from 'lucide-react';
import { LysepAPI, getWebSocketUrl } from '../../../contexts/ApiContext';
import { toast } from 'react-toastify';
import classNames from 'classnames';

const ServiceManagementModal = ({ isOpen, onClose, container, serverId }) => {
    const [activeTab, setActiveTab] = useState('status'); // status, stats, logs
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [status, setStatus] = useState(null);
    const [stats, setStats] = useState(null);
    const [logs, setLogs] = useState('');

    const logsContainerRef = useRef(null);

    // Clean ANSI codes from logs
    const cleanAnsiCodes = (text) => {
        if (!text) return text;
        return text
            .replace(/\x1b\[[0-9;]*m/g, '')
            .replace(/\033\[[0-9;]*m/g, '')
            .replace(/\[[0-9;]*m/g, '')
            .replace(/\x1b/g, '')
            .replace(/\033/g, '');
    };

    useEffect(() => {
        if (isOpen && container) {
            loadAllData();
        } else {
            // Reset state when closed
            setLogs('');
            setStats(null);
            setStatus(null);
            setActiveTab('status');
        }
    }, [isOpen, container]);

    useEffect(() => {
        // Handle tab switching for data loading
        if (isOpen && container) {
            if (activeTab === 'status') loadStatus(container.name);
            else if (activeTab === 'stats') loadStats(container.name);
            else if (activeTab === 'logs') loadLogs(container.name);
        }
    }, [activeTab, isOpen, container]);

    const loadAllData = async () => {
        if (activeTab === 'status') await loadStatus(container.name);
        else if (activeTab === 'stats') await loadStats(container.name);
        else if (activeTab === 'logs') await loadLogs(container.name);
    };

    const loadStatus = async (containerName) => {
        try {
            setLoading(true);
            const response = await LysepAPI.post(`/api/servers/${serverId}/docker/containers/status`, { containerName });
            if (response.data.state) {
                setStatus(response.data.data);
            }
        } catch (error) {
            console.error('Status error:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async (containerName) => {
        try {
            setLoading(true);
            const response = await LysepAPI.post(`/api/servers/${serverId}/docker/containers/stats`, { containerName });
            if (response.data.state) {
                setStats(response.data.data.stats);
            }
        } catch (error) {
            console.error('Stats error:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadLogs = async (containerName) => {
        if (!containerName || !serverId) return;
        try {
            setLoading(true);
            const response = await LysepAPI.post(`/api/servers/${serverId}/docker/containers/logs`, { containerName });
            if (response.data.state) {
                setLogs(cleanAnsiCodes(response.data.data.logs || ''));
            }
        } catch (error) {
            console.error('Logs error:', error);
            toast.error('Log yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    // Removed WebSocket streaming - using simple fetch instead

    // Removed - no longer needed without WebSocket

    const handleContainerAction = async (action) => {
        if (!container || !serverId) return;

        if (action === 'remove' && !window.confirm(`${container.name} silinsin mi? Bu işlem geri alınamaz.`)) {
            return;
        }

        try {
            setActionLoading(true);
            const payload = { containerName: container.name };
            if (action === 'remove') payload.force = true;

            const endpoint = `/api/servers/${serverId}/docker/containers/${action}`;
            const response = await LysepAPI.post(endpoint, payload);

            if (response.data.state) {
                toast.success(response.data.message || 'İşlem başarılı');
                if (action === 'remove') onClose();
                else {
                    // Reload status/logs after action
                    setTimeout(() => loadAllData(), 1000);
                }
            } else {
                toast.error(response.data.message || 'İşlem başarısız');
            }
        } catch (error) {
            toast.error('Hata oluştu');
            console.error(error);
        } finally {
            setActionLoading(false);
        }
    };

    if (!isOpen || !container) return null;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4">
            <div className="w-[95vw] h-[95vh] max-w-[1400px] bg-[#1a1a1a] border border-white/10 rounded-xl flex flex-col shadow-2xl overflow-hidden animate-fadeIn">

                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#252525]">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-[#fd7000]/10 rounded-lg">
                            <Wrench className="w-6 h-6 text-[#fd7000]" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white leading-none">{container.name}</h3>
                            <span className="text-xs text-gray-400">Container Yönetimi</span>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-white/10 bg-[#252525]">
                    <button
                        onClick={() => setActiveTab('status')}
                        className={classNames(
                            "px-6 py-3 text-sm font-medium flex items-center gap-2 transition-colors relative",
                            activeTab === 'status' ? "text-[#fd7000] bg-white/5" : "text-gray-400 hover:text-white hover:bg-white/5"
                        )}
                    >
                        <Activity className="w-4 h-4" />
                        Durum
                        {activeTab === 'status' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#fd7000]" />}
                    </button>
                    <button
                        onClick={() => setActiveTab('stats')}
                        className={classNames(
                            "px-6 py-3 text-sm font-medium flex items-center gap-2 transition-colors relative",
                            activeTab === 'stats' ? "text-[#fd7000] bg-white/5" : "text-gray-400 hover:text-white hover:bg-white/5"
                        )}
                    >
                        <BarChart2 className="w-4 h-4" />
                        İstatistikler
                        {activeTab === 'stats' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#fd7000]" />}
                    </button>
                    <button
                        onClick={() => setActiveTab('logs')}
                        className={classNames(
                            "px-6 py-3 text-sm font-medium flex items-center gap-2 transition-colors relative",
                            activeTab === 'logs' ? "text-[#fd7000] bg-white/5" : "text-gray-400 hover:text-white hover:bg-white/5"
                        )}
                    >
                        <FileText className="w-4 h-4" />
                        Loglar
                        {activeTab === 'logs' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#fd7000]" />}
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto p-6 relative">
                    {/* Loading Overlay within content if needed, but managing per section is better */}

                    {activeTab === 'status' && (
                        <div className="space-y-6">
                            <h4 className="text-lg font-medium text-white mb-4">Container Durumu</h4>
                            {loading ? (
                                <div className="flex justify-center p-10"><Loader2 className="w-8 h-8 text-[#fd7000] animate-spin" /></div>
                            ) : !status ? (
                                <div className="text-center p-10 text-gray-400">Veri alınamadı.</div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <StatusCard label="Durum" value={status.status?.state} icon={<Activity className="w-5 h-5 text-blue-400" />} />
                                    <StatusCard label="IP Adresi" value={status.status?.ip} icon={<Server className="w-5 h-5 text-green-400" />} />
                                    <StatusCard label="Başlatılma" value={status.status?.startedAt || 'N/A'} icon={<Play className="w-5 h-5 text-orange-400" />} />
                                    <StatusCard label="Restart Sayısı" value={status.status?.restartCount || 0} icon={<RotateCcw className="w-5 h-5 text-red-400" />} />
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'stats' && (
                        <div className="space-y-6">
                            <h4 className="text-lg font-medium text-white mb-4">Sistem İstatistikleri</h4>
                            {loading ? (
                                <div className="flex justify-center p-10"><Loader2 className="w-8 h-8 text-[#fd7000] animate-spin" /></div>
                            ) : !stats ? (
                                <div className="text-center p-10 text-gray-400">İstatistik alınamadı.</div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <StatusCard label="CPU" value={stats.cpu} icon={<Cpu className="w-5 h-5 text-purple-400" />} />
                                    <StatusCard label="Memory" value={`${stats.memUsage} / ${stats.memPerc}`} subtext={stats.memPerc} icon={<HardDrive className="w-5 h-5 text-yellow-400" />} />
                                    <StatusCard label="Network I/O" value={stats.netIO} icon={<Activity className="w-5 h-5 text-cyan-400" />} />
                                    <StatusCard label="Block I/O" value={stats.blockIO} icon={<HardDrive className="w-5 h-5 text-gray-400" />} />
                                    <div className="col-span-full">
                                        <StatusCard label="Processes" value={stats.pids} icon={<Server className="w-5 h-5 text-pink-400" />} />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'logs' && (
                        <div className="flex flex-col h-full">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-lg font-medium text-white">Container Logları</h4>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setLogs('')}
                                        className="px-3 py-1.5 rounded-lg border border-white/10 text-gray-400 hover:text-white text-xs transition-colors"
                                    >
                                        Temizle
                                    </button>
                                </div>
                            </div>
                            <div className="flex-1 bg-[#0a0e13] border border-white/5 rounded-lg p-4 overflow-hidden relative">
                                <pre
                                    ref={logsContainerRef}
                                    className="h-full overflow-auto font-mono text-xs leading-relaxed text-gray-300 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent p-2"
                                    dangerouslySetInnerHTML={{
                                        __html: logs ? formatLogs(logs) : '<div class="text-gray-600 italic">Log yüklenmedi. "Yenile" butonuna tıklayın.</div>'
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-white/10 bg-[#252525] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <ActionButton
                            icon={<Play className="w-4 h-4" />}
                            label="Başlat"
                            color="green"
                            onClick={() => handleContainerAction('start')}
                            loading={actionLoading}
                        />
                        <ActionButton
                            icon={<Square className="w-4 h-4" />}
                            label="Durdur"
                            color="red"
                            onClick={() => handleContainerAction('stop')}
                            loading={actionLoading}
                        />
                        <ActionButton
                            icon={<RotateCcw className="w-4 h-4" />}
                            label="Yeniden Başlat"
                            color="blue"
                            onClick={() => handleContainerAction('restart')}
                            loading={actionLoading}
                        />
                        <div className="w-px h-8 bg-white/10 mx-2"></div>
                        <ActionButton
                            icon={<Trash2 className="w-4 h-4" />}
                            label="Sil"
                            color="danger"
                            onClick={() => handleContainerAction('remove')}
                            loading={actionLoading}
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 transition-colors text-sm font-medium"
                        >
                            Kapat
                        </button>
                        <button
                            onClick={loadAllData}
                            className="px-4 py-2 rounded-lg bg-[#fd7000] text-white hover:bg-[#e66500] transition-colors text-sm font-medium flex items-center gap-2"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Yenile
                        </button>
                    </div>
                </div>
            </div>
        </div>
        , document.body);
};

const StatusCard = ({ label, value, icon, subtext }) => (
    <div className="bg-[#2a2a2a] border border-white/5 p-4 rounded-lg flex items-start justify-between">
        <div>
            <p className="text-sm text-gray-400 mb-1">{label}</p>
            <p className="text-lg font-mono text-white">{value}</p>
            {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
        </div>
        {icon && <div className="p-2 bg-white/5 rounded-lg">{icon}</div>}
    </div>
);

const ActionButton = ({ icon, label, color, onClick, loading }) => {
    const colorClasses = {
        green: "bg-green-500/10 text-green-500 hover:bg-green-500/20",
        red: "bg-red-500/10 text-red-500 hover:bg-red-500/20",
        blue: "bg-blue-500/10 text-blue-500 hover:bg-blue-500/20",
        danger: "bg-red-600/10 text-red-600 hover:bg-red-600/20"
    };

    return (
        <button
            onClick={onClick}
            disabled={loading}
            className={classNames(
                "px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                colorClasses[color] || "bg-gray-500/10 text-gray-400"
            )}
            title={label}
        >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}
            <span className="hidden sm:inline">{label}</span>
        </button>
    );
};

const formatLogs = (rawLogs) => {
    // Basic formatting for logs (add span classes for ERROR, INFO, etc.)
    let html = rawLogs
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    html = html
        .replace(/(ERROR|FATAL)/gi, '<span class="text-red-500 font-bold">$1</span>')
        .replace(/(WARN|WARNING)/gi, '<span class="text-yellow-500">$1</span>')
        .replace(/(INFO)/gi, '<span class="text-blue-400">$1</span>')
        .replace(/(DEBUG|TRACE)/gi, '<span class="text-gray-500">$1</span>');

    return html;
};

export default ServiceManagementModal;
